import { ProductApiTypes } from '@hit/policy-admin-types';
import { property, supertypeClass } from '@haventech/amorphic';
import { BaseWaiverFlatExtras, Policy } from '../../../common/js/customer/Policy';
import { Exchange1035FundingPurpose, FeatureFlags, TermOptions } from '../../js/interfaces/CoverpathTypes';
import { getWLProductCode, Product, RiderType } from '../../../common/js/static/Product';
import { Owner } from '../../../common/js/customer/Owner';
import { Utils } from '../../../common/js/Utils';
import { CommonUtils } from '../../../common/js/static/CommonUtils';
import { Address, AddressHolder } from '../../../common/js/customer/Address';
import { Person } from '../../../common/js/customer/Person';
import { Phone } from '../../../common/js/customer/Phone';
import { CoverpathUtils } from '../../js/static/CoverpathUtils';
import { Rider } from '../../../common/js/productService/ProductServiceClient';
import { BaseQuote } from '../../../common/js/quote/BaseQuote';
import { PremiumBreakdown, TLICPremiumBreakdown } from '../../../common/js/customer/PremiumBreakdown';
import { PolicyRider } from '../../../common/js/customer/PolicyRider';
import { ApplicationSourceType } from '../../../common/js/customer/Application';
import { Entity } from '../../../common/js/customer/Entity';
import { PayerTypeOptions } from '../../../common/js/interfaces/CommonTypes';
import { Assumptions } from '../../../common/js/static/Assumptions';
import { CasApplication } from './CasApplication';
import { Customer } from '../../../common/js/customer/Customer';
import { PersonalHistory } from '../../../common/js/customer/PersonalHistory';
import { Exchange1035 } from '../../../common/js/quote/coverpath/Exchange1035';
import { QuoteExchange1035 } from '../../../common/js/quote/coverpath/QuoteExchange1035';
import { ApplicationStateLogic } from '../../../common/js/ApplicationStateLogic';
import { HitAppCtx } from '../../../common/js/orca/HitAppCtx';
import { POLICY_CHANGE_HISTORY_STATUS } from '../../../common/js/static/PolicyChangeHistoryStatus';
import { PolicyAmendmentProperty } from '../../../common/js/static/PolicyAmendmentProperty';
import { ExistingPolicyType } from '../../../common/js/customer/ExistingPolicy';
import { constants as BasicIllustrationConstants } from '../wholelife/basicIllustration/templates/constantsForPdf';
import { LaunchDarklyInstance, LD_APP } from '../../../common/js/launch-darkly/LaunchDarklySingleton';
import { serverOnly } from '../../../common/js/helpers';
import { OwnerUpdateResponse, PolicyAdminUtils } from '../../../common/js/policyAdmin/PolicyAdminUtils';
import { ValidationUtils } from '../../js/static/ValidationUtils';
import { POLICY_TYPES } from '../../../common/js/constants/policy';
import { CPLogger } from '../../../common/js/logger/CPLogger';
import { ValidationService } from '../../angular/services/validation/validation.service';
import { ValidationStatic } from '../../angular/services/validation/validation-static';

@supertypeClass({ toClient: true, toServer: true })
export abstract class CoverpathBasePolicy extends Policy {
  init(customer, type) {
    super.init(customer, type);
    this.initSecondaryAddresseePerson(customer);
    // Set policyUpdatedTime to current if a new policy is created
    this.setPolicyUpdatedTimeToCurrent();
    return this;
  }

  @property({ isLocal: true })
  _termDisplay: string;

  /**
   * designates what is displayed on the UI and is a combination of product and term
   * necessary because we have questions in metadata that do not bind 1 to 1 on properties
   *
   * for unsupported use cases, selections that are not allowed cause a NULL value to be set
   * this is valid as it is equivalent to not answering a question
   *
   * for whole life, it will default to the product that had previously been set
   *
   * for term, it will default to CASTerm because CASTermECP is a modifier on term
   *
   * TODO HL-15192 values/descriptions are being overridden in metadata responses_allowed
   *
   * TODO: HL-45294 - Update this function to set product to CPL10A instead of CPL10 and remove the code in `trigger` function in select-basic.component.ts
   */
  @property({
    isLocal: true,
    values: Object.keys(TermOptions),
    descriptions: TermOptions,
    compositeValidators: ['policyHasValidTerm', 'policyHasValidFaceAmount'],
    comment: 'Available term options for Coverpath',
  })
  set termDisplay(value: string) {
    this._termDisplay = value;
    this.term = value ? parseInt(value) : null;

    if (this.isWholeLifePolicy()) {
      const wlProductCode = getWLProductCode(this.term);
      const wholeLifeProductByTerm = Product.channels.CAS.productCodes[wlProductCode];
      this.product = this.term ? wholeLifeProductByTerm : this.product;
    } else {
      this.product = value && value.match(/ECP/i) ? Product.products.CASTermECP.code : Product.products.CASTerm.code;
    }
  }

  get termDisplay(): string {
    // return null if no term selected(e.g. unsupported usecase)
    if (!this.term) {
      return null;
    }
    if (this.isEcpProduct()) {
      return `${this.term} ECP`;
    } else {
      return this.term.toString();
    }
  }

  @property({ getType: () => Owner })
  ownerList: Array<Owner> = [];

  /**
   * Full options will eventually support:
   * You, [Another Person], [You and another Person (Joint)], [A Trust or Entity]
   */
  @property({
    values: ['you', 'anotherPerson'],
    descriptions: { you: 'Yourself', anotherPerson: 'Another Person' },
    comment: 'the kind of ownership for this policy',
  })
  ownerCategory: string;

  @property({
    values: ['false', 'true'],
    descriptions: { false: 'No', true: 'Yes' },
    compositeValidators: ['policyHasValidFaceAmount'],
    comment: 'Does the policy have an extended conversion period (ECP) option?',
  })
  hasEcp: boolean;

  @property({
    values: [
      'familyProtection',
      'familyIncome',
      'retirement',
      'education',
      'investment',
      'estatePlanning',
      'other',
      'none',
      'unknown',
    ],
    descriptions: {
      familyProtection: 'Family Protection',
      familyIncome: 'Family Income',
      retirement: 'Retirement',
      education: 'Education',
      investment: 'Investment',
      estatePlanning: 'Estate Planning',
      other: 'Other',
      none: 'None',
      unknown: 'Unknown',
    },
    comment: 'What is the main personal reason in buying this policy?',
  })
  personalReasonForInsurance: string;

  @property({
    values: [
      'keyEmployee',
      'stockRedemption',
      'crossPurchase',
      'loanGuarantee',
      '401k',
      '412i',
      '457',
      'deferredCompensation',
      'ESPO',
      'Keogh',
      'profitSharing',
      'splitDollar',
      'targetBenefitPlan',
      'pensionTrust',
      'other',
    ],
    descriptions: {
      'keyEmployee': 'Key Employee',
      'stockRedemption': 'Stock Redemption',
      'crossPurchase': 'Cross Purchase',
      'loanGuarantee': 'Loan Guarantee Coverage',
      '401k': '401k',
      '412i': '412i',
      '457': '457 Plan',
      'deferredCompensation': 'Deferred Compensation',
      'ESPO': 'ESPO',
      'Keogh': 'Keogh',
      'profitSharing': 'Profit Sharing',
      'splitDollar': 'Split Dollar',
      'targetBenefitPlan': 'Target Benefit Plan',
      'pensionTrust': 'Pension Trust',
      'other': 'Other',
    },
    comment: 'What is the main business reason in buying this policy?',
  })
  businessReasonForInsurance: string;

  @property({
    values: ['savings', 'loan', 'other'],
    descriptions: {
      savings: 'No (I am using my income, savings or gifts)',
      loan: 'Yes (I am paying my premiums using a loan)',
      other: 'Yes (I am using other sources)',
    },
    comment:
      'Will the money used to pay the premium on this policy come from a source other than your income, savings/investments or gifts/inheritances?',
  })
  premiumSourceYesNoOther: string;

  @property({
    comment: `Whether to fill in payment information in the app.`,
    values: ['now', 'later'],
    descriptions: {
      now: 'Yes, provide payment details now',
      later: 'No thanks, skip for now',
    },
  })
  paymentInApplication: string;

  @property({
    values: ['false', 'true'],
    descriptions: { false: 'No', true: 'Yes' },
    comment: 'Boolean to indicate if the customer wants to specify a secondary addressee',
  })
  hasSecondaryAddressee: boolean;

  @property({
    comment: 'Policy last updated time used for sorting in agent account center',
  })
  policyUpdatedTime: Date;

  @property({
    persist: false,
    comment: 'Max years for convertible period',
  })
  convertibleMaxYears: number | null;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    comment: "indicates acknowledgement of insured's approval of information",
  })
  insuredInformationApprovalAcknowledgment: boolean;

  @property({
    isLocal: true,
  })
  set hasAlirScheduledPayments(value: boolean) {
    const alirRider: PolicyRider = this.getRider(RiderType.ALIR);
    if (alirRider) {
      alirRider.hasScheduledPayments = value;
      if (alirRider.hasScheduledPayments) {
        this.alirPaymentFrequency = this.paymentFrequency;
      }
    }
  }
  get hasAlirScheduledPayments(): boolean {
    const alirRider: PolicyRider = this.getRider(RiderType.ALIR);
    return alirRider ? alirRider.hasScheduledPayments : null;
  }

  @property({
    isLocal: true,
    rule: ['currency'],
  })
  set alir1035ExchangeAmount(value: number) {
    const alirRider: PolicyRider = this.getRider(RiderType.ALIR);
    if (alirRider && alirRider.exchange1035) {
      alirRider.exchange1035.cashValueTransfer = value;
    }
  }
  get alir1035ExchangeAmount(): number {
    const alirRider: PolicyRider = this.getRider(RiderType.ALIR);
    return alirRider && alirRider.exchange1035 ? alirRider.exchange1035.cashValueTransfer : null;
  }

  @property({
    isLocal: true,
  })
  set hasAlirLumpSum(value: boolean) {
    const alirRider: PolicyRider = this.getRider(RiderType.ALIR);
    if (alirRider) {
      alirRider.hasLumpSum = value;
    }
  }
  get hasAlirLumpSum(): boolean {
    const alirRider: PolicyRider = this.getRider(RiderType.ALIR);
    return alirRider ? alirRider.hasLumpSum : null;
  }

  @property({
    isLocal: true,
  })
  set alir1035Purpose(value: string) {
    const alirRider: PolicyRider = this.getRider(RiderType.ALIR);
    if (alirRider && alirRider.exchange1035) {
      alirRider.exchange1035.applyTowards = value;
    }
  }
  get alir1035Purpose(): string {
    const alirRider: PolicyRider = this.getRider(RiderType.ALIR);
    return alirRider && alirRider.exchange1035 ? alirRider.exchange1035.applyTowards : null;
  }

  @property({
    isLocal: true,
    rule: ['currencyWithCents', 'required'],
  })
  set additionalLifeInsurancePremiumBase(value: number) {
    const alirRider: PolicyRider = this.getRider(RiderType.ALIR);
    if (alirRider) {
      const isMonthly = CommonUtils.makePaymentFrequencySafeForPolicy(alirRider.paymentFrequency) === 'monthly';
      alirRider.premiumBase = value;
      alirRider.amount = isMonthly && value ? alirRider.premiumBase * 12 : alirRider.premiumBase;
    }
  }
  get additionalLifeInsurancePremiumBase(): number {
    const alirRider: PolicyRider = this.getRider(RiderType.ALIR);
    return alirRider ? alirRider.premiumBase : null;
  }

  @property({
    isLocal: true,
  })
  set hasAlirRider(value: boolean) {
    if (value) {
      this.addRider(RiderType.ALIR);
    } else {
      this.removeRider(RiderType.ALIR);
    }
  }
  get hasAlirRider(): boolean {
    const alirRider: PolicyRider = this.getRider(RiderType.ALIR);
    return !!alirRider;
  }

  hasAlirRiderTrigger() {
    if (!this.hasAlirRider && this.insured.existingPolicies.hasCashValueReplacement()) {
      this.insured.existingPolicies.removeCashValueReplacement();
    }
  }

  @property({
    isLocal: true,
    rule: ['currencyWithCents'],
    resetOnError: true,
  })
  set additionalLifeInsuranceAmount(value: number) {
    const alirRider: PolicyRider = this.getRider(RiderType.ALIR);
    if (alirRider) {
      alirRider.amount = value;
    }
  }
  get additionalLifeInsuranceAmount(): number {
    const alirRider: PolicyRider = this.getRider(RiderType.ALIR);
    return alirRider ? alirRider.amount : null;
  }

  @property({
    isLocal: true,
    rule: ['numericPositive', 'numericWhole'],
  })
  set alirPlannedYears(value: number) {
    const alirRider: PolicyRider = this.getRider(RiderType.ALIR);
    if (alirRider) {
      alirRider.plannedYears = value;
    }
  }
  get alirPlannedYears(): number {
    const alirRider: PolicyRider = this.getRider(RiderType.ALIR);
    return alirRider ? alirRider.plannedYears : null;
  }

  @property({
    isLocal: true,
  })
  set alirPaymentFrequency(value: string) {
    const alirRider: PolicyRider = this.getRider(RiderType.ALIR);
    if (alirRider && alirRider.paymentFrequency !== value) {
      alirRider.paymentFrequency = value;
      alirRider.premiumBase = null;
      alirRider.amount = null;
    }
  }
  get alirPaymentFrequency(): string {
    const alirRider: PolicyRider = this.getRider(RiderType.ALIR);
    return alirRider ? alirRider.paymentFrequency : null;
  }

  @property({
    isLocal: true,
    rule: ['currencyWithCents'],
  })
  set alirLumpSumAmount(value: number) {
    const alirRider: PolicyRider = this.getRider(RiderType.ALIR);
    if (alirRider) {
      alirRider.lumpSumAmount = value;
    }
  }
  get alirLumpSumAmount(): number {
    const alirRider: PolicyRider = this.getRider(RiderType.ALIR);
    return alirRider ? alirRider.lumpSumAmount : null;
  }

  @property({
    comment: 'Giact Web Debit Verification check',
  })
  isGiactWebDebitValid: boolean;

  @property({
    comment: 'Giact Web Debit Verification response code',
  })
  giactWebDebitResponseCode: string;

  @property({
    comment: 'Last attempted verification combo, prevents duplicate attempts',
  })
  lastWebDebitAttemptCombination: string;

  @property({
    comment: 'Last attempted account number that results in a letter',
  })
  giactLetterLastFour: string;

  @property({
    isLocal: true,
  })
  set alirLumpSumUses1035(value: boolean) {
    const alirRider: PolicyRider = this.getRider(RiderType.ALIR);
    if (alirRider) {
      alirRider.lumpSumUses1035 = value;
      if (value && !alirRider.exchange1035) {
        if (this.application.quote && this.application.quote.quoteExchange1035) {
          const quoteExchange1035: QuoteExchange1035 = this.application.quote.quoteExchange1035;
          alirRider.exchange1035 = Exchange1035.new(
            null,
            quoteExchange1035.costBasis,
            quoteExchange1035.policyType,
            quoteExchange1035.applyTowards,
            quoteExchange1035.expectedArrival,
            quoteExchange1035.isMec
          );
        } else {
          alirRider.exchange1035 = Exchange1035.new(
            null,
            null,
            ExistingPolicyType.WHOLE,
            Exchange1035FundingPurpose.ALIR_ONLY,
            Exchange1035.EXPECTED_ARRIVAL_DEFAULT_MONTHS,
            false
          );
        }
      } else {
        // when we are removing the exchange 1035 from the rider we have to call a server side function (remove1035 in PolicyRider.ts)
        // this function is async but we cant make the setter async or the trigger.
        // For this reason, I have added it in the controller.ts (client/agent) in publicSave using registerPostServerCallback()
        // Need to keep the property as well
      }
    }
  }

  get alirLumpSumUses1035(): boolean {
    const alirRider: PolicyRider = this.getRider(RiderType.ALIR);
    return alirRider ? alirRider.lumpSumUses1035 : null;
  }

  alirLumpSumUses1035Trigger() {
    if (!this.alirLumpSumUses1035 && this.insured.existingPolicies.hasCashValueReplacement()) {
      this.insured.existingPolicies.removeCashValueReplacement();
    }
  }

  removeExchange1035() {
    this.alirLumpSumUses1035 = false;
    if (!this.hasAlirScheduledPayments && !this.hasAlirLumpSum) {
      this.hasAlirRider = false; // untick ALIR rider in policy info section if no children are ticked
    }
  }

  alir1035PurposeTrigger() {
    const alirRider: PolicyRider = this.getRider(RiderType.ALIR);
    if (alirRider && alirRider.exchange1035) {
      if (alirRider.exchange1035.applyTowards === Exchange1035FundingPurpose.PREMIUM_AND_ALIR) {
        alirRider.exchange1035.expectedArrival = 0;
      } else if (alirRider.exchange1035.applyTowards === Exchange1035FundingPurpose.ALIR_ONLY) {
        // use the quote expected arrival if any, otherwise default to 3
        if (this.application.quote && this.application.quote.quoteExchange1035) {
          alirRider.exchange1035.expectedArrival = this.application.quote.quoteExchange1035.expectedArrival;
        } else {
          alirRider.exchange1035.expectedArrival = Exchange1035.EXPECTED_ARRIVAL_DEFAULT_MONTHS;
        }
      }
    }
  }

  alir1035ExchangeAmountTrigger() {
    const alirRider: PolicyRider = this.getRider(RiderType.ALIR);
    if (alirRider && alirRider.exchange1035) {
      alirRider.exchange1035.setCostBasisToCashValueTransfer();
    }

    const existingPolicies = this.insured.existingPolicies;
    if (!this.alir1035ExchangeAmount && existingPolicies.hasCashValueReplacement()) {
      existingPolicies.removeCashValueReplacement();
    } else if (this.alir1035ExchangeAmount) {
      if (!existingPolicies.hasCashValueReplacement()) {
        existingPolicies.addCashValueReplacement(this.alir1035ExchangeAmount);
      } else {
        existingPolicies.getCashValueReplacement().cashValueTransfer = this.alir1035ExchangeAmount;
      }
    }
  }

  // Will be overriden in both CoverpathPolicy and MMDirectPolicy
  isTeleCMIRequired(): boolean {
    return false;
  }

  @property({
    isLocal: true,
  })
  get alirRider(): PolicyRider {
    return (this.riders || []).find((rider) => {
      return rider.type === RiderType.ALIR;
    });
  }

  set alirRider(rider: PolicyRider) {
    if (rider) {
      this.riders.push(rider);
    } else {
      this.removeRider(RiderType.ALIR);
    }
  }

  includeSecondaryAddresseeForm(): boolean {
    return this.isSecondaryAddresseeState(this.customer) && this.hasSecondaryAddressee;
  }

  includeSecondaryAddresseeFormForOwnerOtherThanInsured(): boolean {
    return this.includeSecondaryAddresseeForm() && this.isOwnerOtherThanInsuredAndNotJuvenile();
  }

  includeSecondaryAddresseeFormForOwnerSameAsInsured(): boolean {
    return this.includeSecondaryAddresseeForm() && this.isOwnerSameAsInsuredOrJuvenile();
  }

  /**
   * @returns {Date} when representing cut-off for when term products can be converted to WL, or null if N/A
   * @TODO: Needs unit test - HL-10677
   */
  getConvertibilityUntilDate(cutOffConvertibleAge: number): Date {
    const age: number = this.insured.getAge();
    const effectiveDate = this.effectiveDate;
    const yearsInThePast = new Date().getFullYear() - effectiveDate.getFullYear();
    const issueAge = age - yearsInThePast;
    let conversionPeriod: number;

    if (this.isWholeLifePolicy()) {
      return null;
    }

    if (!issueAge || issueAge > cutOffConvertibleAge) {
      return null;
    }

    if (!effectiveDate) {
      return null;
    }

    let maxConversionPeriod: number;
    if (this.isEcpProduct()) {
      maxConversionPeriod = this.convertibleMaxYears ? this.convertibleMaxYears : 10;
    } else {
      maxConversionPeriod = 10;
    }

    const cutOff = cutOffConvertibleAge - maxConversionPeriod;

    if (issueAge < cutOff) {
      conversionPeriod = maxConversionPeriod;
    } else {
      // two years of convertability is awarded if you are at the border of the age bracket
      if (issueAge > cutOffConvertibleAge - 2) {
        conversionPeriod = 2;
      } else {
        conversionPeriod = cutOffConvertibleAge - issueAge;
      }
    }

    const conversionDate = Utils.offsetDate(effectiveDate, conversionPeriod, 'years');

    if (conversionDate <= Utils.now()) {
      return null;
    }

    return conversionDate;
  }

  getSignerFullName(): string {
    if (this.isOwnerSameAsInsured()) {
      return this.insured.person.getFullName();
    } else if (this.owner.getIsEntityOrTrust()) {
      return this.ownerEntity.authorizedSigner.getFullName();
    } else {
      return this.owner.getFullNameOfOwner();
    }
  }

  getEntityName(): string {
    if (this.owner.getIsEntityOrTrust()) {
      return this.trustOrOtherEntity.getFullName() || '';
    }
    return '';
  }

  getEntitySignerTitle(): string {
    if (this.owner.getIsEntityOrTrust()) {
      const title = Utils.get(this, 'trustOrOtherEntity.authorizedSigner.title');
      return title || '';
    }
    return '';
  }

  getOtherOwnerName(): string {
    if (this.otherOwner) {
      return this.otherOwner.getFullName();
    }
    return '';
  }

  getPolicyNumberString(): string {
    if (this.isPartOfJointApp()) {
      return `${this.policyNumber}, ${this.application.secondaryPolicy.policyNumber}`;
    }

    return this.policyNumber;
  }

  getPolicyTitle() {
    if (this.isWholeLifePolicy()) {
      return BasicIllustrationConstants.ProductNames[this.product];
    }

    return 'Term Life ' + this.term + ' Years';
  }

  getPolicyAddresses(): Array<AddressHolder> {
    const ret: Array<AddressHolder> = [];
    // get addresses insured
    ret.push({
      title: `Insured's Residential Address`,
      address: this.insured.person.residentialAddress,
      name: this.insured.person.getFullName(),
      prop: 'customer.application.primaryPolicy.insured.address',
    });
    ret.push({
      title: `Insured's Verification Address`,
      address: this.insured.person.verificationAddress,
      name: this.insured.person.getFullName(),
      prop: 'customer.application.primaryPolicy.insured.verificationAddress',
    });

    if (!this.isOwnerSameAsInsured()) {
      // get addresses owner
      if (this.ownerPerson) {
        ret.push({
          title: `Owner's Residential Address`,
          address: this.ownerPerson.residentialAddress,
          name: this.owner.getFullNameOfOwner(),
          prop: 'customer.application.primaryPolicy.ownerPersonResidentialAddress',
        });
      } else {
        ret.push({
          title: `Owner's Mailing Address`,
          address: this.owner.mailingAddress,
          name: this.owner.getFullNameOfOwner(),
          prop: 'customer.application.primaryPolicy.trustOrOtherEntity.mailingAddress',
        });
        ret.push({
          title: `Owner's Address`,
          address: this.owner.legalAddress,
          name: this.owner.getFullNameOfOwner(),
          prop: 'customer.application.primaryPolicy.trustOrOtherEntity.legalAddress',
        });
        ret.push({
          title: `Owner's Legal Address`,
          address: this.ownerEntity.ownerAddress,
          name: this.owner.getFullNameOfOwner(),
          prop: 'customer.application.primaryPolicy.trustOrOtherEntity.ownerAddress',
        });
      }
    }

    if (this.isJointOwnership() && this.otherOwner) {
      ret.push({
        title: `Joint Owner's Residential Address`,
        address: this.otherOwner.residentialAddress,
        name: this.otherOwner.getFullName(),
        prop: 'customer.application.primaryPolicy.otherOwner.residentialAddress',
      });
    }

    if (this.contingentOwnerPerson) {
      ret.push({
        title: `Contingent Owner's Residential Address`,
        address: this.contingentOwnerPerson.residentialAddress,
        name: this.contingentOwnerPerson.getFullName(),
        prop: 'customer.application.primaryPolicy.contingentOwnerPerson.residentialAddress',
      });
    }
    // get addresses beneficiary
    this.primaryBeneficiary.forEach((bene, index) => {
      ret.push({
        title: `Primary Beneficiary ${index + 1} Address`,
        address: bene.getAddress(),
        name: bene.getFullName(),
        prop: `customer.application.primaryPolicy.primaryBeneficiary[${index}]`,
      });
    });
    this.contingentBeneficiary.forEach((bene, index) => {
      ret.push({
        title: `Contingent Beneficiary ${index + 1} Address`,
        address: bene.getAddress(),
        name: bene.getFullName(),
        prop: `customer.application.primaryPolicy.contingentBeneficiary[${index}]`,
      });
    });
    // get addresses payee
    if (this.isOwnerAlsoPayer === 'false') {
      ret.push({
        title: `Payer's Address`,
        address: this.payer.mailingAddress,
        name: this.payer.getFullNameOfOwner(),
        prop: 'customer.application.primaryPolicy.payer.mailingAddress',
      });
    }
    if (this.hasSecondaryAddressee) {
      ret.push({
        title: `Secondary Payer's Address`,
        address: this.secondaryAddresseePerson.residentialAddress,
        name: this.secondaryAddresseePerson.getFullName(),
        prop: 'customer.application.primaryPolicy.secondaryAddresseePerson.residentialAddress',
      });
    }

    // TODO add signer and family member addresses once new filing is merged HL-26865

    return ret;
  }

  insuredTypeTrigger() {
    // We are adding this because of a special case in coverpath metadata
    // where insuredType is getting overwritten. set it back.
    if (this.insuredType === null && this._selfInsured === 'true') {
      this.insuredType = 'you';
    } else if (this.insuredType === 'you') {
      this._selfInsured = 'true';

      /**
       * if insuredType is changed to 'you'
       * then the only possible value for ownerType = 'you'
       */
      this.ownerType = 'you';
      this.isJointOwnerSomeoneElse = null;

      /**
       * if insuredType is changed to 'you'
       * AND ownerClassification is 'trust'
       * AND is not Life Bridge (exception to the rule that "you" have to own the policy)
       * clear the ownerClassification value
       * because 'trust' is no longer a possible option
       */
      if (this.ownerClassification === 'trust' && this.isNotLifeBridge()) {
        this.ownerClassification = null;
        this.ownerClassificationTrigger();
      } else if (this.ownerClassification === 'joint') {
        this.ownerClassificationTrigger();
      }
    } else {
      this._selfInsured = 'false';

      if (this.insuredType === 'generalbusiness' && this.ownerClassification !== 'trust') {
        /**
         * if user changes insuredType = 'Business Relationship'
         * and ownerClassification != 'trust'
         * then set it and run the trigger
         */
        this.ownerClassification = 'trust';
        this.ownerClassificationTrigger();
      }
    }
    this.ownerTypeTrigger();

    /**
     * If insured is a juvenile and the user selected an insuredType value that is NOT 'son', 'daughter', 'maleward', or 'femaleward'
     * then reset the hasSigner value so the user has to re-answer the question
     *
     * This is to disallow the signer from being anything other than the Parent or Legal Guardian of the juvenile insured
     */
    if (
      this.insuredIsJuvenile() &&
      this.insured.hasSigner === false &&
      !['son', 'daughter', 'maleward', 'femaleward'].includes(this.insuredType)
    ) {
      this.insured.hasSigner = null;
    }

    super.insuredTypeTrigger();

    /**
     * if ownerType = invalid selection after insuredType change
     * then reset it and ownerTypeSelection to null
     */
    if (!this.ownerTypeValues().includes(this.ownerType)) {
      this.ownerTypeSelection = null;
      this.ownerType = null;
    }
    // in case the owner is not insured reset owner's email if they match
    if (!this.isOwnerSameAsInsured()) {
      this.resetOwnerEmailIfItMatchesInsured();
    }

    // Set policyUpdatedTime to current time, if insuredType of this policy is changed.
    this.setPolicyUpdatedTimeToCurrent();
  }

  isJointOwnerSomeoneElseTrigger() {
    if (this.isJointOwnerSomeoneElse === 'false') {
      this.otherOwner = undefined;
    } else {
      this.otherOwner = Person.new(this.customer);
      this.otherOwner.phone = Phone.new(this.customer);
      this.otherOwner.residentialAddress = Address.new(this.customer);
      this.otherOwner.residentialAddress.state = this.ownerPersonResidentialAddress.state;
      this.otherOwner.mailingAddress = Address.new(this.customer);
    }
  }

  ownerClassificationTrigger() {
    if (this.ownerClassification === 'joint') {
      this.otherOwner = Person.new(this.customer);
      this.otherOwner.phone = Phone.new(this.customer);
      this.otherOwner.residentialAddress = Address.new(this.customer);
      this.otherOwner.mailingAddress = Address.new(this.customer);
    } else {
      this.otherOwner = undefined;
    }

    // there is no guarantee that owner will be populated before joint,
    // because owner 1 can each be the insured
    if (this.ownerClassification === 'joint' && !this.otherOwner.residentialAddress.state) {
      this.otherOwner.residentialAddress.state = this.ownerPersonResidentialAddress.state || this.insured.address.state;
    }
    this.onSelfOwnerChange();
    /**
     * in case the owner is not insured and
     * the insured is not a juvenile OR the owner is not the juvenile signer
     * reset owner's email if they match
     */
    if (!this.isOwnerSameAsInsured() && (!this.insuredIsJuvenile() || this.isOwnerOtherThanSigner())) {
      this.resetOwnerEmailIfItMatchesInsured();
    }

    // Set policyUpdatedTime to current time, if ownerClassification of this policy is changed.
    this.setPolicyUpdatedTimeToCurrent();
  }

  // in case the owner and insured emails match set the owner email to null
  resetOwnerEmailIfItMatchesInsured(): void {
    if (this.insured.person.email && this.ownerPerson && this.ownerPerson.email) {
      const insuredEmail = this.insured.person.email.toLowerCase();
      const ownerEmail = this.ownerPerson.email.toLowerCase();
      if (insuredEmail === ownerEmail) {
        this.ownerPerson.email = null;
      }
    }
  }

  ownerTypeTrigger() {
    // Tactical fix to the owner insured issue seen in prod.
    // Long term fix requires big refactor and fixing of associated issues.  See:
    // https://massmutual.atlassian.net/browse/HL-13169
    if (this.ownerType === null && this._selfOwner === 'true') {
      this.ownerType = 'you';
    }
    super.ownerTypeTrigger();
  }

  hasEcpTrigger() {
    if (this.isTermLifePolicy()) {
      if (this.isCoverpathPolicy()) {
        this.product = this.hasEcp ? Product.products.CASTermECP.code : Product.products.CASTerm.code;
      } else if (this.isMMDirectPolicy()) {
        this.product = this.hasEcp ? Product.products.MMDirectTermECP.code : Product.products.MMDirectTerm.code;
      }
    }
  }

  private doesPolicySupportConversion(policy: Policy): boolean {
    return (
      policy &&
      policy.isTermLifePolicy() &&
      (policy.term.valueOf() === 15 ||
        policy.term.valueOf() === 20 ||
        policy.term.valueOf() === 25 ||
        policy.term.valueOf() === 30)
    );
  }

  //age 18-45 for term 30 && tobacco, 18-54 for others.
  isEcpEligibleForPrimaryPolicy(): boolean {
    return this.doesPolicySupportConversion(this) && CoverpathUtils.isEcpEligible(this);
  }

  isEcpEligibleForSecondaryPolicy(): boolean {
    return (
      this.doesPolicySupportConversion(this.application.secondaryPolicy) &&
      CoverpathUtils.isEcpEligible(this.application.secondaryPolicy as CoverpathBasePolicy)
    );
  }

  isWopEligibleForPrimaryPolicy(): boolean {
    return CommonUtils.isWopEligible(this);
  }

  isWopEligibleForSecondaryPolicy(): boolean {
    return CommonUtils.isWopEligible(this.application.secondaryPolicy as CoverpathBasePolicy);
  }

  isValidRTRPolicy() {
    const rtrAmount = this.rtrRiderAmount || 0;
    return (
      this.isWholeLifePolicy() &&
      ValidationStatic.isValidRtrAge(this.product, this.faceAmount, rtrAmount, this.insured.person.dob).result
    );
  }

  isValidGIRPolicy() {
    return this.isWholeLifePolicy() && ValidationUtils.isValidGirAge(this.insured.person.dob).result;
  }

  updateRidersPremium(riders: Rider[] = []) {
    riders.forEach(({ type, premium, waiver }) => {
      const rider = this.riders.find((rider) => rider.type === type);
      rider.premiumBase = premium;
      rider.premiumWaiver = waiver;
    });
  }

  updateAlirRiderPremium(): void {
    if (this.hasAlirRider) {
      let alirPremium: number = this.alirRider.amount;
      if (
        alirPremium &&
        this.paymentFrequency.toLowerCase() === ProductApiTypes.PaymentFrequency.Monthly.toLowerCase()
      ) {
        alirPremium = Utils.round(alirPremium / 12, 2);
      }
      this.alirRider.premiumBase = alirPremium;
    }
  }

  updateWithQuoteInfo(quote: BaseQuote): void {
    const createOrUpdate = (pbObject, pbClass) => {
      if (pbObject) {
        pbObject.base = quote.premium;
      } else {
        pbObject = pbClass.new(quote.premium, null, null, this);
      }

      return pbObject;
    };

    this.faceAmount = quote.faceAmount;
    this.term = quote.term;
    this.paymentFrequency = CommonUtils.makePaymentFrequencySafeForPolicy(quote.paymentFrequency);
    this.carrier = 'CAS';
    this.waiver = !!quote.waiverOfPremium;
    this.tLICPremiumBreakdown = createOrUpdate(this.tLICPremiumBreakdown, TLICPremiumBreakdown);
    this.finalAlgoPremiumBreakdown = createOrUpdate(this.finalAlgoPremiumBreakdown, PremiumBreakdown);
    this.premiumBreakdown = createOrUpdate(this.premiumBreakdown, PremiumBreakdown);
    const waiverOfPremiumAmount = quote.waiverOfPremiumAmount;
    this.premiumBreakdown.waiverOfPremiumAmount = waiverOfPremiumAmount && waiverOfPremiumAmount.valueOf();
    this.premiumBreakdown.waiver = this.waiver && waiverOfPremiumAmount ? waiverOfPremiumAmount.valueOf() : 0;
    this.effectiveDate = quote.effectiveDate;
  }

  getApplicationState() {
    let state = this.insured.address.state;

    if ((!state || !this.isOwnerSameAsInsured()) && this.ownerPersonResidentialAddress.state) {
      state = this.ownerPersonResidentialAddress.state;
    }
    return state;
  }

  isOwnerSameAsInsured(): boolean {
    return (
      (!this.insuredType && !this.ownerType) || (this.insuredType === 'you' && this.ownerType === this.insuredType)
    );
  }

  /**
   * If part of a joint app keeps secondary policy payment frequency in sync with primary
   */
  paymentFrequencyTrigger() {
    if (this.isPartOfJointApp() && this.isPrimary) {
      this.application.secondaryPolicy.paymentFrequency = this.paymentFrequency;
    }
    if (this.hasAlirRider) {
      this.alirPaymentFrequency = this.paymentFrequency;
    }
  }

  termTrigger() {
    // isEcpEligibleForPrimaryPolicy() checks if 'this' is ECP eligible so not necessarily the primary policy
    if (this.hasEcp && !this.isEcpEligibleForPrimaryPolicy()) {
      /**
       * Not eligible for ECP, so question will not display
       *
       * Need to reset hasEcp and product now so when the reclaculation of premiums occurs, we send the correct product to the Product Service Endpoint
       */
      this.hasEcp = false;
      this.product = this.isCoverpathPolicy() ? Product.products.CASTerm.code : Product.products.MMDirectTerm.code;
    }
  }

  showBasedOffFnAndAge(fn: number, age: number): boolean {
    let ret = true;
    switch (fn) {
      case 0:
        ret = this.isInsuredYoungerThanAge(age);
        return typeof ret !== 'undefined' ? ret : false;
      case 1:
        ret = this.isInsuredYoungerThanOrEqualToAge(age);
        return typeof ret !== 'undefined' ? ret : false;
      case 2:
        ret = this.isInsuredOlderThanAge(age);
        return typeof ret !== 'undefined' ? ret : true;
      case 3:
        ret = this.isInsuredOlderThanOrEqualToAge(age);
        return typeof ret !== 'undefined' ? ret : true;
      default:
        return ret;
    }
  }

  isInsuredYoungerThanAge(age: number): boolean {
    if (this.insured.person.dob) {
      return CommonUtils.isYoungerThanAge(new Date(this.insured.person.dob), age);
    }
    throw new Error('Insured DOB is undefined');
  }

  isInsuredOlderThanAge(age: number): boolean {
    if (this.insured.person.dob) {
      return CommonUtils.isOlderThanAge(new Date(this.insured.person.dob), age);
    }
    throw new Error('Insured DOB is undefined');
  }

  isPersonNotUSCitizenResidesInUS(personString: string): boolean {
    let person: Person;
    switch (personString) {
      case 'insured':
        person = this.insured.person;
        break;
      case 'signer':
        person = this.insured.signer;
        break;
      case 'owner':
        person = this.ownerPerson;
        break;
      case 'contingentOwner':
        person = this.contingentOwnerPerson;
        break;
      case 'otherOwner':
        person = this.otherOwner;
        break;
      default:
    }

    return person && Utils.exists(person.residencyStatus) && !person.isCitizen();
  }

  ownerTypeValues() {
    const values = [];

    if (this.isLifeBridge()) {
      // if LifeBridge, owner is always Trust
      values.push('trust');
      return values;
    }

    if (this.ownerClassification === 'trust') {
      /**
       * will remove 'you' when bug is fixed to set ownerType correctly
       * when 'A Trust or Entity' is selected for ownerClassification
       * HL-24022
       */
      values.push('you', 'trust');
      return values;
    }

    switch (this.insuredType) {
      case 'you':
        values.push('you');
        break;
      case 'spouse':
        values.push('spouse');
        break;
      case 'partnerfiance':
        values.push('partnerfiance');
        break;
      case 'son':
      case 'daughter':
        values.push('father', 'mother');
        break;
      case 'grandchild':
        values.push('grandparent');
        break;
      case 'father':
      case 'mother':
        values.push('son', 'daughter');
        break;
      case 'maleward':
      case 'femaleward':
        values.push('malelegalguardian', 'femalelegalguardian');
        break;
      case 'malelegalguardian':
      case 'femalelegalguardian':
        values.push('maleward', 'femaleward');
        break;
      case 'exhusband':
      case 'exwife':
        values.push('exhusband', 'exwife');
        break;
      case 'generalbusiness':
        values.push('trust');
        break;
      case 'employee':
      case 'ownershareholder':
      case 'otherbusiness':
        values.push('employee', 'ownershareholder', 'otherbusiness');
        break;
      case 'trustbeneficiary':
      case 'trustgrantor':
      case 'othertrust':
        values.push('trustbeneficiary', 'trustgrantor', 'othertrust');
        break;
      case 'notYou':
        values.push(
          'spousePartnerFiance',
          'grandparent',
          'parent',
          'legalGuardian',
          'child',
          'exSpouse',
          'businessAssociate',
          'trust'
        );
        break;
      default:
        values.push('you');
    }

    return values;
  }

  /**
   * Returns true if this a Coverpath, MMDirect, or LifeBridge Policy and is using the 2020 metadata or 2021 metadata
   */
  is2020CoverpathOrMMD(): boolean {
    return (
      Utils.isCoverpathPlatform(this.getChannelCode()) &&
      this.appMetadataVersion > CoverpathUtils.deprecatedMetadata2019Version
    );
  }

  /**
   * Returns true if this a Coverpath, MMDirect, or LifeBridge Policy and is using the 2021 metadata
   */
  is2021CoverpathOrMMD(): boolean {
    if (this.getApplicationState() === 'CA') {
      return false;
    }

    return (
      Utils.isCoverpathPlatform(this.getChannelCode()) &&
      this.appMetadataVersion > CoverpathUtils.deprecatedMetadata2020Version
    );
  }

  async createClonedPolicy(keepOriginal?: boolean, sourceType?: ApplicationSourceType): Promise<Policy> {
    const newPolicy = (await super.createClonedPolicy(keepOriginal, sourceType)) as CoverpathBasePolicy;

    let appMetadataVersion: number = this.appMetadataVersion;
    if (!appMetadataVersion && this.type === POLICY_TYPES.PRIMARY) {
      let sourceAppNumber: string = this.application.sourceAppNumber;
      /**
       * this policy object does not have an appMetadataVersion yet, so look at application where application number = this.application.sourceAppNumber
       *
       * it is possible even this source application's primary policy does not have an appmMetadataVersion yet
       * so keep trying source applications until you find an appMetadataVersion
       * if none is found, then continue, there is no work to do here
       */
      while (!appMetadataVersion && sourceAppNumber) {
        const result = await CasApplication.getKnex().client.raw(
          `select a._id , "sourceAppNumber", p."appMetadataVersion"
            from application a inner join policy p
              on p.application_id  = a."_id"
            and p."type" = '${POLICY_TYPES.PRIMARY}'
            and "applicationNumber" = ?`,
          [sourceAppNumber]
        );
        if (result.rows.length === 0) {
          // no application found for given "sourceAppNumber" so break out of while loop
          break;
        }
        appMetadataVersion = result.rows[0].appMetadataVersion;
        sourceAppNumber = result.rows[0].sourceAppNumber;
      }
    }

    if (appMetadataVersion) {
      if (appMetadataVersion <= CoverpathUtils.deprecatedMetadata2019Version) {
        this.resetJuvenilePropertiesForNewMetadata(newPolicy);
      }
      if (appMetadataVersion < CoverpathUtils.metadataVersionWithExtraIDQuestions) {
        this.initUpdatedMetadataProperties(newPolicy);
      }
    }

    if (!this.isOwnerSameAsInsured() && (!this.insuredIsJuvenile() || this.isOwnerOtherThanSigner())) {
      (newPolicy as CoverpathBasePolicy).resetOwnerEmailIfItMatchesInsured();
    }
    if (
      [
        Product.products.CPL10.code,
        Product.products.CPL15.code,
        Product.products.CPL20.code,
        Product.products.CPL65.code,
        Product.products.CPL100.code,
      ].includes(newPolicy.product)
    ) {
      newPolicy.product = Product.products[`${newPolicy.product}A`].code;
      newPolicy.premiumBreakdown.base = null;
    }

    newPolicy.setPolicyUpdatedTimeToCurrent();

    return newPolicy;
  }

  /**
   * If we are cloning a CP/MMD/LB policy which is 2019 Filing or prior we need to carefully reset a few properties.
   * Prior to 2020_Filing these were preset for juveniles; however now we dont even show these questions and we need
   * to reset their values to null
   * For example, a 2019-Filing Juvenile policy was setting question "B00015" (Are you employed?): financials.employed
   * to 'false'. Now, this question is hidden for Juveniles, and the respective property is null
   */
  private resetJuvenilePropertiesForNewMetadata(newPolicy) {
    // there are 3 ages over which we (now) show questions in the metadata: 16.5, 14 and 10
    // Look for this function in the CAS ICC/nonICC meta: showBasedOffFnAndAge
    const insured = newPolicy.insured;
    const insuredPerson = insured.person;
    const financials = insured.financials;
    const riskFactors = insured.riskFactors;
    const isYoungerThan16AndAHalf = CommonUtils.isYoungerThanAge(new Date(newPolicy.insured.person.dob), 16.5);
    if (isYoungerThan16AndAHalf) {
      financials.employed =
        financials.isHomeMaker =
        financials.unemploymentDuration =
        financials.priorOccupation =
        financials.priorIncome =
        financials.reemploymentExpectancy =
        financials.reemploymentExpectancyDetails =
        financials.hasBankruptcyHistory =
          null;
      financials.employedTrigger();

      riskFactors.mvDrivingWhileSuspended =
        riskFactors.mvLicenseSuspended =
        riskFactors.armedForces =
        riskFactors.medicalDischarge =
          null;
      riskFactors.mvDrivingWhileSuspendedTrigger();
    }
    const isYoungerThan14 = CommonUtils.isYoungerThanAge(new Date(newPolicy.insured.person.dob), 14);
    if (isYoungerThan14) {
      riskFactors.mvDUI =
        riskFactors.mvAccident =
        riskFactors.isPilot =
        riskFactors.hasAvocations =
        riskFactors.isPregnant =
          null;
    }
    const isYoungerThan10 = CommonUtils.isYoungerThanAge(new Date(newPolicy.insured.person.dob), 10);
    if (isYoungerThan10) {
      riskFactors.isFelonios =
        riskFactors.whenLastSmokedOrUsedMarijuana =
        riskFactors.useTobaccoCessationMeds =
        riskFactors.dontDrinkAlcohol =
          null;
      insured.personalHistory.hadDrugUse = insured.personalHistory.hadAlcoholAbuse = null;
    }
  }

  private initUpdatedMetadataProperties(newPolicy: CoverpathBasePolicy) {
    if (newPolicy.insuredIsJuvenileHasSigner()) {
      newPolicy.insured.signer.init(newPolicy.customer);
    }

    // init payer
    if (newPolicy.showPayerEntity()) {
      (<Entity>newPolicy.payer).authorizedSigner.init(newPolicy.customer);
    } else if (newPolicy.showPayerPerson()) {
      (<Person>newPolicy.payer).residentialAddress = Address.new(newPolicy.customer);
      newPolicy.payer.phone = Phone.new(newPolicy.customer);
    }

    //grantor/trustee stuff
    if (newPolicy.ownerEntity) {
      newPolicy.ownerEntity.authorizedSigner.init(newPolicy.customer);
      newPolicy.ownerEntity.authorizedTrusteeList.forEach((trustee) => {
        if (trustee.trusteePerson) {
          trustee.trusteePerson.init(newPolicy.customer);
        } else if (trustee.entityAuthorizedSigner) {
          trustee.entityAuthorizedSigner.init(newPolicy.customer);
        }
      });
      newPolicy.ownerEntity.trustGrantorList.forEach((grantor) => {
        if (grantor.type === 'other') {
          grantor.grantorPerson.residentialAddress = Address.new(newPolicy.customer);
          grantor.grantorPerson.phone = Phone.new(newPolicy.customer);
        }
      });
    }
  }

  isJointOwnership(): boolean {
    return this.ownerClassification === 'joint';
  }

  isJointOwnerNotEnabled() {
    return !this.isJointOwnerEnabledFlag;
  }

  isNotJointOwnership(): boolean {
    return !this.isJointOwnership();
  }

  showJointOwnershipDetails(): boolean {
    return this.isJointOwnership() && (this.insuredType === 'you' || this.isJointOwnerSomeoneElse === 'true');
  }

  jointOwnerIsInsured(): boolean {
    return this.isJointOwnership() && this.isJointOwnerSomeoneElse === 'false';
  }

  isAnotherOwnership(): boolean {
    return this.ownerClassification === 'another';
  }

  isNotAnotherOwnership(): boolean {
    return !this.isAnotherOwnership();
  }

  /**
   * when owner is also payer, premiumPayer field should be set to "owner"
   * when owner is not payer, premiumPayer field should be wiped
   */
  isOwnerAlsoPayerTrigger(): void {
    if (this.isOwnerAlsoPayer === 'true') {
      this.premiumPayer = 'owner';
      if (this.owner instanceof Entity) {
        this.premiumPayer = PayerTypeOptions.entity;
      }
    } else {
      this.premiumPayer = undefined;
    }

    this.isAutoPay = true;
  }

  /**
   * switch between types of payer - Person or Entity
   */
  premiumPayerTrigger(): void {
    if (this.premiumPayer === PayerTypeOptions.entity) {
      this.payer = Entity.new(this.customer, false, this);
      this.payer.mailingAddress = Address.new();
    } else if (this.premiumPayer === PayerTypeOptions.anotherPerson) {
      this.payer = Person.new(this.customer);
      this.payer.mailingAddress = Address.new();
      (<Person>this.payer).residentialAddress = Address.new(this.customer);
      this.payer.phone = Phone.new(this.customer);
    }
  }

  isSecondaryAddresseeState(customer: Customer): boolean {
    if (this.customer.isCoverpath2019MetadataEnabled()) {
      // all states on 2019 filing have this feature
      return true;
    }
    const state = this.getApplicationState();
    const channel = this.getChannelCode();
    const secondaryAddresseeStates = Assumptions.secondaryAddresseeDesignatedStates[channel];
    return secondaryAddresseeStates && state in secondaryAddresseeStates;
  }

  getMMFullName() {
    return 'MassMutual Life Insurance Company';
  }

  getPaymentAccountType(): string {
    let accountType = '';
    accountType += this.showPayerPerson() ? 'Personal ' : 'Business ';
    accountType += this.paymentAccountType === '1' ? 'Savings' : 'Checking';
    return accountType;
  }

  isNotSelfOwnerAndSelfInsured(): boolean {
    return !(this.selfOwner === 'true' && this.insuredType === 'you');
  }

  isOwnerSameAsInsuredOrJuvenile(): boolean {
    return this.isOwnerSameAsInsured() || (this.insuredIsJuvenile() && this.isOwnerSameAsSigner());
  }

  isOwnerSameAsInsuredOrJuvenileSingleOwner(): boolean {
    return this.isOwnerSameAsInsuredOrJuvenile() && !this.hasSecondOwner();
  }

  isOwnerSameAsInsuredOrJuvenileHasSecondOwner(): boolean {
    return this.isOwnerSameAsInsuredOrJuvenile() && this.hasSecondOwner();
  }

  isOwnerOtherThanInsuredAndNotJuvenile(): boolean {
    return !this.isOwnerSameAsInsuredOrJuvenile();
  }

  hasSecondOwner(): boolean {
    return this.isJointOwnership();
  }

  isOwnerSameAsInsuredOrJuvenile2021Filing(): boolean {
    return this.isOwnerSameAsInsuredOrJuvenile() && this.is2021CoverpathOrMMD();
  }

  isOwnerSameAsInsuredOrJuvenile2021FilingSingleOwner(): boolean {
    return this.isOwnerSameAsInsuredOrJuvenile2021Filing() && !this.hasSecondOwner();
  }

  isOwnerSameAsInsuredOrJuvenile2021FilingHasSecondOwner(): boolean {
    return this.isOwnerSameAsInsuredOrJuvenile2021Filing() && this.hasSecondOwner();
  }

  isOwnerOtherThanInsuredAndNotJuvenile2021Filing(): boolean {
    return this.isOwnerOtherThanInsuredAndNotJuvenile() && this.is2021CoverpathOrMMD();
  }

  isOwnerOtherThanInsuredAndNotJuvenile2021FilingSingleOwner(): boolean {
    return this.isOwnerOtherThanInsuredAndNotJuvenile2021Filing() && !this.hasSecondOwner();
  }

  isOwnerOtherThanInsuredAndNotJuvenile2021FilingHasSecondOwner(): boolean {
    return this.isOwnerOtherThanInsuredAndNotJuvenile2021Filing() && this.hasSecondOwner();
  }

  isOwnerSameAsInsuredOrJuvenile2020Filing(): boolean {
    return this.isOwnerSameAsInsuredOrJuvenile() && this.is2020CoverpathOrMMD() && !this.is2021CoverpathOrMMD();
  }

  isOwnerSameAsInsuredOrJuvenile2020FilingSingleOwner(): boolean {
    return this.isOwnerSameAsInsuredOrJuvenile2020Filing() && !this.hasSecondOwner();
  }

  isOwnerSameAsInsuredOrJuvenile2020FilingHasSecondOwner(): boolean {
    return this.isOwnerSameAsInsuredOrJuvenile2020Filing() && this.hasSecondOwner();
  }

  isOwnerOtherThanInsuredAndNotJuvenile2020Filing(): boolean {
    return this.isOwnerOtherThanInsuredAndNotJuvenile() && this.is2020CoverpathOrMMD() && !this.is2021CoverpathOrMMD();
  }

  isOwnerOtherThanInsuredAndNotJuvenile2020FilingSingleOwner(): boolean {
    return this.isOwnerOtherThanInsuredAndNotJuvenile2020Filing() && !this.hasSecondOwner();
  }

  isOwnerOtherThanInsuredAndNotJuvenile2020FilingHasSecondOwner(): boolean {
    return this.isOwnerOtherThanInsuredAndNotJuvenile2020Filing() && this.hasSecondOwner();
  }

  isOwnerSameAsInsuredOrJuvenileNot2020Filing(): boolean {
    return this.isOwnerSameAsInsuredOrJuvenile() && !this.is2020CoverpathOrMMD();
  }

  isOwnerSameAsInsuredOrJuvenileNot2020FilingSingleOwner(): boolean {
    return this.isOwnerSameAsInsuredOrJuvenileNot2020Filing() && !this.hasSecondOwner();
  }

  isOwnerSameAsInsuredOrJuvenileNot2020FilingHasSecondOwner(): boolean {
    return this.isOwnerSameAsInsuredOrJuvenileNot2020Filing() && this.hasSecondOwner();
  }

  isOwnerOtherThanInsuredAndNotJuvenileNot2020Filing(): boolean {
    return this.isOwnerOtherThanInsuredAndNotJuvenile() && !this.is2020CoverpathOrMMD();
  }

  isOwnerOtherThanInsuredAndNotJuvenileNot2020FilingSingleOwner(): boolean {
    return this.isOwnerOtherThanInsuredAndNotJuvenileNot2020Filing() && !this.hasSecondOwner();
  }

  isOwnerOtherThanInsuredAndNotJuvenileNot2020FilingHasSecondOwner(): boolean {
    return this.isOwnerOtherThanInsuredAndNotJuvenileNot2020Filing() && this.hasSecondOwner();
  }

  isOwnerSameAsSigner() {
    return !this.isOwnerOtherThanSigner();
  }

  insuredIsJuvenileDefinedSigner() {
    return (
      this.insuredIsJuvenile() &&
      this.insured.hasSigner !== undefined &&
      this.insured.hasSigner !== null &&
      this.isOwnerSameAsSigner()
    );
  }

  insuredIsJuvenileUndefinedSigner() {
    return (
      this.insuredIsJuvenile() &&
      (this.insured.hasSigner === undefined || this.insured.hasSigner === null || this.isOwnerOtherThanSigner())
    );
  }

  getInsuredOrRepresentative(): string {
    if (this.insuredIsJuvenile()) {
      return this.insured.signer.getFullName();
    }

    return super.getInsuredOrRepresentative();
  }

  getProposedName(): string {
    if (this.insuredIsJuvenile()) {
      return this.insured.person.getFullName();
    }
  }

  isOwnerSameAsInsuredAndTLIC() {
    return this.isOwnerSameAsInsuredOrJuvenile() && this.isTLIC();
  }

  isOwnerOtherThanInsuredAndTLIC() {
    return this.isOwnerOtherThanInsuredAndNotJuvenile() && this.isTLIC();
  }

  isOwnerInVa() {
    // TODO investigate params to metadata B-36319
    let ownerState = '';
    if (this.owner) {
      if (this.owner instanceof Person) {
        ownerState = this.owner.residentialAddress.state;
      } else {
        ownerState = this.owner.legalAddress.state;
      }
    }
    return this.isOwnerOtherThanInsured() && ownerState === 'VA';
  }

  isInsuredInVa() {
    return this.application.primaryPolicy.insured.address.state === 'VA';
  }

  isOwnerSameAsInsuredNoReplace() {
    return this.isOwnerSameAsInsuredOrJuvenile() && this.shouldSkipReplacement();
  }

  isOwnerOtherThanInsuredNoReplace() {
    return this.isOwnerOtherThanInsuredAndNotJuvenile() && this.shouldSkipReplacement();
  }

  isOwnerSameAsInsuredHasExistingPolicies(): boolean {
    return (
      this.isOwnerSameAsInsuredOrJuvenile() &&
      !!this.application.primaryPolicy.insured.existingPolicies.hasExistingPolicies &&
      this.shouldSkipReplacement()
    );
  }

  isOwnerOtherThanInsuredHasExistingPolicies(): boolean {
    return (
      this.isOwnerOtherThanInsuredAndNotJuvenile() &&
      !!this.application.primaryPolicy.insured.existingPolicies.hasExistingPolicies &&
      this.shouldSkipReplacement()
    );
  }

  isOwnerSameAsInsuredNoExistingPolicies(): boolean {
    return (
      this.isOwnerSameAsInsuredOrJuvenile() &&
      !this.application.primaryPolicy.insured.existingPolicies.hasExistingPolicies
    );
  }

  isOwnerOtherThanInsuredNoExistingPolicies(): boolean {
    return (
      this.isOwnerOtherThanInsuredAndNotJuvenile() &&
      !this.application.primaryPolicy.insured.existingPolicies.hasExistingPolicies
    );
  }

  requiresReplacementOwnerSameAsInsuredForm(): boolean {
    return !this.shouldSkipReplacement() && this.isOwnerSameAsInsuredOrJuvenile();
  }

  requiresReplacementOwnerOtherThanInsuredForm(): boolean {
    return !this.shouldSkipReplacement() && this.isOwnerOtherThanInsuredAndNotJuvenile();
  }

  requiresExternalReplacementOwnerSameAsInsuredForm(): boolean {
    return (
      this.insured.existingPolicies.externalReplacedOrFundedPolicies.length && this.isOwnerSameAsInsuredOrJuvenile()
    );
  }

  requiresInternalReplacementOwnerSameAsInsuredForm(): boolean {
    return (
      this.insured.existingPolicies.internalReplacedOrFundedPolicies.length && this.isOwnerSameAsInsuredOrJuvenile()
    );
  }

  requiresExternalReplacementOwnerOtherThanInsuredForm(): boolean {
    return (
      this.insured.existingPolicies.externalReplacedOrFundedPolicies.length &&
      this.isOwnerOtherThanInsuredAndNotJuvenile()
    );
  }

  requiresInternalReplacementOwnerOtherThanInsuredForm(): boolean {
    return (
      this.insured.existingPolicies.internalReplacedOrFundedPolicies.length &&
      this.isOwnerOtherThanInsuredAndNotJuvenile()
    );
  }

  requiresReplacementOwnerSameAsInsuredAndHas2ndInsurerForm(): boolean {
    return (
      !!this.requiresReplacementOwnerSameAsInsuredForm() &&
      this.application.primaryPolicy.insured.existingPolicies.hasSecondInsurer()
    );
  }

  requiresReplacementOwnerSameAsInsuredAndHas3rdInsurerForm(): boolean {
    return (
      !!this.requiresReplacementOwnerSameAsInsuredForm() &&
      this.application.primaryPolicy.insured.existingPolicies.hasThirdInsurer()
    );
  }

  requiresReplacementOwnerOtherThanInsuredAndHas2ndInsurerForm(): boolean {
    return (
      !!this.requiresReplacementOwnerOtherThanInsuredForm() &&
      this.application.primaryPolicy.insured.existingPolicies.hasSecondInsurer()
    );
  }

  requiresReplacementOwnerOtherThanInsuredAndHas3rdInsurerForm(): boolean {
    return (
      !!this.requiresReplacementOwnerOtherThanInsuredForm() &&
      this.application.primaryPolicy.insured.existingPolicies.hasThirdInsurer()
    );
  }

  insuredIsNotJuvenile(): boolean {
    return this.insuredIsJuvenile() === false;
  }

  insuredIsJuvenileHasSigner(): boolean {
    return this.insuredIsJuvenile() && this.isOwnerOtherThanSigner();
  }

  //Form 1 for 1st and 2nd Internal Policy
  requiresInternalReplacementOwnerSameAsInsuredForm1(): boolean {
    return (
      this.isOwnerSameAsInsuredOrJuvenile() &&
      this.application.primaryPolicy.insured.existingPolicies.internalTermReplacedPolicies.length > 0 &&
      !this.shouldSkipReplacement()
    );
  }

  //Form 1 for 1st and 2nd Internal Policy
  requiresInternalReplacementOwnerOtherThanInsuredForm1(): boolean {
    return (
      this.isOwnerOtherThanInsuredAndNotJuvenile() &&
      this.application.primaryPolicy.insured.existingPolicies.internalTermReplacedPolicies.length > 0 &&
      !this.shouldSkipReplacement()
    );
  }

  //Form 2 for 3rd and 4th Internal Policy
  requiresInternalReplacementOwnerSameAsInsuredForm2(): boolean {
    return (
      this.isOwnerSameAsInsuredOrJuvenile() &&
      this.application.primaryPolicy.insured.existingPolicies.internalTermReplacedPolicies.length > 2 &&
      !this.shouldSkipReplacement()
    );
  }

  //Form 2 for 3rd and 4th Internal Policy
  requiresInternalReplacementOwnerOtherThanInsuredForm2(): boolean {
    return (
      this.isOwnerOtherThanInsuredAndNotJuvenile() &&
      this.application.primaryPolicy.insured.existingPolicies.internalTermReplacedPolicies.length > 2 &&
      !this.shouldSkipReplacement()
    );
  }

  requiresSpecialHandling(): boolean {
    let toReturn = false;
    if (this.isThirdPartyPayer()) {
      //Third Party Payer
      toReturn = true;
    }
    if (this.isNonCitizenSpecialHandling()) {
      //Non Citizen
      toReturn = true;
    }

    if (this.collaterallyAssigned) {
      //Collateral Assignment
      toReturn = true;
    }

    if (this.owner.getIsEntityOrTrust()) {
      //Entity/Trust Owner
      toReturn = true;
    }

    if (this.isOwnerOtherThanSigner()) {
      toReturn = true;
    }

    return toReturn;
  }

  shouldShowAllInsuredOptions(): boolean {
    return this.isPartOfJointApp() || this.insuredIsNotJuvenile();
  }

  shouldShowLimitedInsuredOptions(): boolean {
    return this.isNotPartOfJointApp() && this.insuredIsJuvenile();
  }

  getOwnerSigningPerson(): Person {
    return this.owner.getIsEntityOrTrust() ? this.ownerEntity.authorizedSigner : this.ownerPerson;
  }

  prefillJuvenileQuestions(): void {
    const insured = this.insured;
    /**
     * Some questions will not be hidden depending on the insured's age in the 2020 Coverpath filing
     * so we need to continue to pre-fill the following answers in the Medical section
     */
    insured.riskFactors.weightLoss = 'NoIhavenot';
    insured.riskFactors.disabled = false;
    insured.personalHistory.hadNoCondition = true;
    insured.personalHistory.hadBloodTransfusion = false;
    insured.personalHistory.hadBloodTransfusionTrigger();
    insured.personalHistory.hadHeartSurgery = false;
    insured.personalHistory.hadHeartSurgeryTrigger();
    insured.personalHistory.hasProstatectomy = false;
    insured.personalHistory.hasProstatectomyTrigger();
    insured.personalHistory.hadMastectomy = false;
    insured.personalHistory.hadMastectomyTrigger();
    insured.personalHistory.hadECG = false;
    insured.personalHistory.hadInsuranceRestricted = false;
    insured.personalHistory.hadDisabilityClaim = false;
    insured.personalHistory.hadExamOther = false;
    insured.personalHistory.hadFullDiagnosisExclHIV = 'No';
    insured.personalHistory.hadRXOther = false;
    insured.personalHistory.hadRXOtherTrigger();
  }

  getTermPolicyIfExists(): Policy {
    if (this.isPartOfJointApp()) {
      return this.application.secondaryPolicy;
    }

    return this.application.primaryPolicy;
  }

  // only used for the whole life form
  getWOPTotalForPA() {
    if (!this.waiver) {
      return '';
    }
    let premBd = this.premiumBreakdown;
    if (premBd.base === 0) {
      premBd = this.appliedForPremiumBreakdown;
    }
    let totalWaiverPremium = this.riders.reduce((total, rider) => total + rider.premiumWaiver, premBd.waiver);
    // this arithmetic is EVIL, monthly * 12 does NOT equal the annual premium
    // this is an acceptable circumstance for PA docusign ONLY!
    if (this.paymentFrequency.toLowerCase() === 'monthly') {
      totalWaiverPremium *= 12;
    }
    return totalWaiverPremium > 0 ? Utils.formatCurrency(totalWaiverPremium, 2) : '';
  }

  getPARiderMonthly() {
    let totalWaiverPremium = this.waiverPremium || this.premiumBreakdown.waiver;
    if (totalWaiverPremium === 0) {
      totalWaiverPremium = this.appliedForPremiumBreakdown.waiver;
    }
    if (this.paymentFrequency.toLowerCase() === 'annual') {
      totalWaiverPremium *= PremiumBreakdown.YEARLY_TO_MONTHLY_RATIO;
    }
    return (
      'The cost is included in the premium for the policy.\n' +
      (this.waiver ? Utils.formatCurrency(totalWaiverPremium, 2) : '')
    );
  }

  getPolicyTypeDisplay(): string {
    return `${this.productType} Life Insurance`;
  }

  requiresAmendmentOfApplicationOwnerIsInsuredForm(): boolean {
    const primaryRequiresAmendment = this.hasPendingAmendment();
    const secondaryRequiresAmendment =
      this.application && this.application.secondaryPolicy && this.application.secondaryPolicy.hasPendingAmendment();
    return !!(
      (primaryRequiresAmendment || secondaryRequiresAmendment) &&
      this.isAccelerated() &&
      this.isOwnerSameAsInsuredOrJuvenile()
    );
  }

  requiresAmendmentOfApplicationOwnerIsNotInsuredForm(): boolean {
    const primaryRequiresAmendment = this.hasPendingAmendment();
    const secondaryRequiresAmendment =
      this.application && this.application.secondaryPolicy && this.application.secondaryPolicy.hasPendingAmendment();
    return !!(
      (primaryRequiresAmendment || secondaryRequiresAmendment) &&
      this.isAccelerated() &&
      this.isOwnerOtherThanInsuredAndNotJuvenile()
    );
  }

  requiresInsurabilityStatementOwnerIsInsuredForm(): boolean {
    return this.isAccelerated() && this.isOwnerSameAsInsuredOrJuvenile();
  }

  requiresInsurabilityStatementOwnerIsNotInsuredForm(): boolean {
    return this.isAccelerated() && this.isOwnerOtherThanInsuredAndNotJuvenile();
  }

  requiresNWMReplacementFormsOwnerIsInsuredSingleOwner(): boolean {
    return false; // NWM replacement not required if no agent
  }

  requiresNWMReplacementFormsOwnerIsInsuredHasSecondOwner(): boolean {
    return false; // NWM replacement not required if no agent
  }

  requiresNWMReplacementFormsOwnerIsNotInsuredSingleOwner(): boolean {
    return false; // NWM replacement not required if no agent
  }

  requiresNWMReplacementFormsOwnerIsNotInsuredHasSecondOwner(): boolean {
    return false; // NWM replacement not required if no agent
  }

  requiresCIRDisclosureOwnerIsInsuredForm(): boolean {
    return false;
  }

  requiresCIRDisclosureOwnerIsNotInsuredForm(): boolean {
    return false;
  }

  isCollaterallyAssigned(): boolean {
    return this.collaterallyAssigned && this.isOwnerSameAsInsuredOrJuvenile();
  }

  isCollaterallyAssignedInsuredOtherThanOwner(): boolean {
    return this.collaterallyAssigned && this.isOwnerOtherThanInsuredAndNotJuvenile();
  }

  isNonIncorporatedEntity(): boolean {
    return this.trustOrOtherEntity && this.trustOrOtherEntity.type === 'nonIncorporatedEntity';
  }

  isEntityOwnershipStateNotMA(): boolean {
    return (
      this.trustOrOtherEntity && this.trustOrOtherEntity.type !== 'trust' && this.trustOrOtherEntity.trustSitus !== 'MA'
    );
  }

  isEntityOwnershipStateMA(): boolean {
    return (
      this.trustOrOtherEntity && this.trustOrOtherEntity.type !== 'trust' && this.trustOrOtherEntity.trustSitus === 'MA'
    );
  }

  isIndividualTrustee(): boolean {
    return this.trustOrOtherEntity &&
      this.trustOrOtherEntity.trustName &&
      this.trustOrOtherEntity.designatedTrustee &&
      this.trustOrOtherEntity.designatedTrustee.type === 'Individual'
      ? true
      : false;
  }

  isEntityTrustee(): boolean {
    return this.trustOrOtherEntity &&
      this.trustOrOtherEntity.trustName &&
      this.trustOrOtherEntity.designatedTrustee &&
      this.trustOrOtherEntity.designatedTrustee.type === 'Entity'
      ? true
      : false;
  }

  isMedicalConditionSelected(): boolean {
    const personalHistory: PersonalHistory = this.insured.personalHistory;
    return personalHistory
      ? personalHistory.hadHeartCondition ||
          personalHistory.hadHighCholesterol ||
          personalHistory.hadCancer ||
          personalHistory.hadBloodDisorder ||
          personalHistory.hadBrainDisorder ||
          personalHistory.hadEmotionalDisorder ||
          personalHistory.hadEENT ||
          personalHistory.hadRespiratoryDisorder ||
          personalHistory.hadDigestiveDisorder ||
          personalHistory.hadBoneMuscularDisorder ||
          personalHistory.hadBMDArthritis ||
          personalHistory.hasDiabetesThyroid ||
          personalHistory.hadUrninaryTrackDisorder ||
          personalHistory.hadSkinDisorder ||
          personalHistory.hadImmunoDeficiency
      : false;
  }

  isNoMedicalConditionSelected(): boolean {
    return !this.isMedicalConditionSelected();
  }

  isEcpProductWithPendingAmendment(): boolean {
    return (
      this.productWithPendingAmendment() === Product.products.CASTermECP.code ||
      this.product === Product.products.MMDirectTermECP.code ||
      this.product === Product.products.MMTRADTermECP.code
    );
  }

  hasAlirRiderWithPendingAmendment(): boolean {
    return false;
  }

  hasGirRiderWithPendingAmendment(): boolean {
    return false;
  }

  hasRtrRiderWithPendingAmendment(): boolean {
    return false;
  }

  hasPendingAmendment(): boolean {
    if (this.application.primaryPolicy.getPendingAmendments().length > 0) {
      return true;
    }
    if (this.application.secondaryPolicy && this.application.secondaryPolicy.getPendingAmendments().length > 0) {
      return true;
    }
    return false;
  }

  _setIssueProperties(tzdt, premBd: BaseWaiverFlatExtras) {
    super._setIssueProperties(tzdt, premBd);

    // if current policy is primary policy of joint app, set same effectiveDate on secondaryPolicy
    if (this.isPartOfJointApp() && this.isPrimary) {
      const application = this.application;
      if (!application.secondaryPolicy.effectiveDate) {
        application.secondaryPolicy.effectiveDate = this.effectiveDate;
      }
      application.secondaryPolicy._setFreeLookEndsOn();
      HitAppCtx.get.exnCtx.dbTxn.setDirty(application.secondaryPolicy);
    }
  }

  protected updateWithPolicyInfo(policy: Policy): void {}
  isNewYorkApplication(): boolean {
    return this.getApplicationState() === 'NY';
  }

  isCAApplication(): boolean {
    return this.getApplicationState() === 'CA';
  }

  _setFreeLookEndsOn() {
    const today = Utils.now();

    this.freeLookPeriodDays = <number>ApplicationStateLogic.freeLookPeriod(this);
    this.freeLookEndsOn = Utils.offsetDate(today, this.freeLookPeriodDays, 'days');
  }

  //This method is called during AI2/ES2 as an activity and must still set the signed date in the case of backdating
  onApplicationSignedAU(signedDate, signedState, effectiveDate, premBd: BaseWaiverFlatExtras) {
    this.applicationSignedDate = signedDate;
    this.applicationSignedState = signedState;

    this.effectiveDate = this.effectiveDateWithPendingAmendment();
    const effectiveDatePCH = this.getPendingAmendments().find(
      (pch) =>
        pch.status === POLICY_CHANGE_HISTORY_STATUS.PENDING && pch.property === PolicyAmendmentProperty.EFFECTIVE_DATE
    );

    if (effectiveDatePCH) {
      //For Fluildless/AU policies amendments are applied after docusign
      //So for unbackdated policies we do not want to overwrite effectiveDate
      effectiveDatePCH.status = POLICY_CHANGE_HISTORY_STATUS.COMPLETE;
      HitAppCtx.get.exnCtx.dbTxn.setDirty(effectiveDatePCH);
    }

    //If OPs has backdated, effective date will not be null -- in that case do not update the effective date
    if (!this.effectiveDate) {
      const tzdt = this.calculateEffDate(effectiveDate);
      this._setIssueProperties(tzdt, premBd);
    } else {
      this._setFreeLookEndsOn();
    }

    this.aUEffectiveDate = new Date(this.effectiveDate.getTime());
    this.paramedRequiredBy = Utils.offsetDate(signedDate, Assumptions.MUWParamedDays, 'days');
    this.finalDecisionRequiredBy = null;
  }

  onPolicyIssueConfirmed(confirmDate, premBd: BaseWaiverFlatExtras) {
    if (!this.effectiveDate || confirmDate < this.effectiveDate) {
      const tzdt = this.calculateEffDate(confirmDate);
      this._setIssueProperties(tzdt, premBd);
    } else {
      this._setFreeLookEndsOn();
      HitAppCtx.get.exnCtx.dbTxn.setDirty(this);
    }
  }

  isWholeLifePolicyWithPendingAmendment(): boolean {
    return Product.products[this.productWithPendingAmendment()].type === Product.productTypes.Whole;
  }

  isTermLifePolicyWithPendingAmendment(): boolean {
    return Product.products[this.productWithPendingAmendment()].type === Product.productTypes.Term;
  }

  insuredBornInTheUSA(): boolean {
    return !!(this.insured && this.insured.person && this.insured.person.bornInTheUSA());
  }

  // Set policyUpdatedTime to current time,
  // so that this policy will show up on the top of the AAC list view.
  setPolicyUpdatedTimeToCurrent(): void {
    this.policyUpdatedTime = Utils.now();
  }

  hasTeleCMIMetadata(): boolean {
    return this.appMetadataVersion >= CoverpathUtils.teleCMIMetadataVersion;
  }

  ownerAndInsuredEmailsShouldBeSame(): boolean {
    if (this.insuredIsJuvenile()) {
      return !this.insured.hasSigner;
    } else {
      return this.isOwnerSameAsInsured();
    }
  }

  paymentInApplicationTrigger() {
    this.isAutoPay = true;
    this.giactWebDebitResponseCode = null;
    this.isGiactWebDebitValid = null;
    this.lastWebDebitAttemptCombination = null;
  }

  isAgentSubmittedPolicy() {
    return false;
  }

  /**
   * If this policy exists in PA, trigger off an update on the owner's email in PA
   *
   * @returns - Status of update request (was request fired off successfully)
   *  - If False, notify DigiOps, if True, then no action
   */
  @serverOnly()
  async updateOwnerEmailInPAIfExists(email: string): Promise<OwnerUpdateResponse> {
    let ownerId: string | undefined;
    try {
      ownerId = await PolicyAdminUtils.getOwnerIdFromPA(this, email);
    } catch (err) {
      return OwnerUpdateResponse.ErrorInPACall;
    }
    if (ownerId) {
      return PolicyAdminUtils.updateOwnerEmail(email, this, ownerId);
    }
    return OwnerUpdateResponse.NonExistentInPAOrUpdateNotNeeded;
  }

  @serverOnly()
  static async batchUpdateOwnerEmailInPA(
    policies: Array<CoverpathBasePolicy>,
    email: string
  ): Promise<PolicyNumberResultTuple[]> {
    const resultArr: PolicyNumberResultTuple[] = [];
    for await (const policy of policies) {
      const res = await policy.updateOwnerEmailInPAIfExists(email);
      resultArr.push({ policyNumber: policy.policyNumber, status: res });
    }

    return resultArr;
  }
}

export type PolicyNumberResultTuple = {
  policyNumber: string;
  status: OwnerUpdateResponse;
};
